import React, { useCallback, useEffect, useRef, useState } from 'react'

import cx from 'classnames'
import { clone, isEmpty } from 'ramda'
import { useDispatch, useSelector } from 'react-redux'

import {
  ReportFilter,
  ReportFilterGroup,
  ReportFilterOperation,
} from '@driftt/api-types'
import { Button } from '@driftt/dds'
import { faFilter } from '@driftt/dds.icons/regular/faFilter'
import { Chip, Div, QuickActionButton, Span } from '@driftt/tide-core'

import { FilterableEntityType } from 'hooks/useAvailableFilters'
import { useFilterableEntities } from 'hooks/useFilterableEntities'
import useOnClickOutside from 'hooks/useOnClickOutside'
import * as FilterActions from 'modules/filter/actions'
import * as FilterSelectors from 'modules/filter/selectors'
import { teams } from 'modules/teams/selectors'
import { ReportPage } from 'types/pages'
import { trackEvent } from 'utils/tracking'

import CompoundFilterRow from './CompoundFilterRow'
import { filterToComponent } from './CompoundFilterRow/util'

import './style.css'

export interface IReportFilterBarProps {
  reportType: ReportPage
  showAdvancedFilters?: boolean
  showReportFilterBar?: boolean
  showDatePicker?: boolean
}

const DEFAULT_FILTER: ReportFilter = Object.freeze({
  property: 'teammate',
  operation: ReportFilterOperation.IN,
  values: [],
})

const DEFAULT_FILTER_GROUP: ReportFilterGroup = Object.freeze({
  shouldMatch: 1,
  negateFilterGroup: false,
  filters: [DEFAULT_FILTER],
})

interface ICompoundFilterBodyProps {
  reportType: ReportPage
  compoundFilter: ReportFilterGroup
  availableFilterableEntities: FilterableEntityType[]
  setCompoundFilter: (filter: ReportFilterGroup) => void
  onDeleteFinalRow: () => void
  allowOnlyOne?: boolean
}

const CompoundFilterBody: React.FC<ICompoundFilterBodyProps> = ({
  reportType,
  compoundFilter,
  setCompoundFilter,
  availableFilterableEntities,
  onDeleteFinalRow,
  allowOnlyOne = false,
}) => {
  const displayableFilter =
    compoundFilter && compoundFilter.filters.length > 0
      ? compoundFilter
      : clone(DEFAULT_FILTER_GROUP)

  const updateCompoundFilter = useCallback(
    (newFilter: ReportFilter, index: number) => {
      const newFilters = displayableFilter.filters
      newFilters.splice(index, 1, newFilter)
      const newCompoundFilter = { ...displayableFilter, filters: newFilters }
      setCompoundFilter(newCompoundFilter)
    },
    [displayableFilter, setCompoundFilter]
  )

  const addFilterRow = useCallback(() => {
    const newFilters = [...displayableFilter.filters, clone(DEFAULT_FILTER)]
    const newCompoundFilter = { ...displayableFilter, filters: newFilters }
    setCompoundFilter(newCompoundFilter)
  }, [displayableFilter, setCompoundFilter])

  const deleteFilterRow = useCallback(
    (index) => {
      const newFilters = displayableFilter.filters
      newFilters.splice(index, 1)
      const newCompoundFilter = { ...displayableFilter, filters: newFilters }
      setCompoundFilter(newCompoundFilter)
      newFilters.length === 0 && onDeleteFinalRow()
    },
    [displayableFilter, setCompoundFilter, onDeleteFinalRow]
  )

  return (
    <Div
      className="compound-filter--body"
      data-ocweb-id="ReportsCompoundFilterBody"
    >
      <Div className="compound-filter--table-container">
        {displayableFilter && displayableFilter.filters.length > 1 && (
          <Div className="compound-filter--and-block">
            <Div className="compound-filter--and">And</Div>
          </Div>
        )}
        <Div className="compound-filter--filters">
          {displayableFilter &&
            displayableFilter.filters.map((filter, index) => (
              <CompoundFilterRow
                key={JSON.stringify(filter) + index}
                reportType={reportType}
                filter={filter}
                updateFilters={(newFilter) =>
                  updateCompoundFilter(newFilter, index)
                }
                onDelete={() => deleteFilterRow(index)}
                availableFilterableEntities={availableFilterableEntities}
              />
            ))}
        </Div>
      </Div>
      {!allowOnlyOne && (
        <Div
          className="compound-filter--filter-add"
          data-ocweb-id="ReportsCompoundFilterAddButton"
        >
          <QuickActionButton
            iconName="plus"
            text="Add filter"
            onClick={addFilterRow}
          />
        </Div>
      )}
    </Div>
  )
}

interface IReportsCompoundFilterProps {
  reportType: ReportPage
  availableFilterableEntities: FilterableEntityType[]
  compoundFilter: ReportFilterGroup
  validFilters: ReportFilterGroup
  setCompoundFilter: (filter: ReportFilterGroup) => void
  allowOnlyOne?: boolean
}

const sendChatReportFilterTrackingEvent = (
  reportType: ReportPage,
  filters: ReportFilter[]
) => {
  if (reportType !== ReportPage.Conversation || filters.length === 0) {
    return
  }

  trackEvent('REPORTS_UPDATED_CHAT_FILTERS', {
    filters,
  })
}

const ReportsCompoundFilter: React.FC<IReportsCompoundFilterProps> = ({
  reportType,
  availableFilterableEntities,
  compoundFilter,
  validFilters,
  setCompoundFilter,
  allowOnlyOne = false,
}) => {
  const [showBody, setShowBody] = useState<boolean>(false)

  const activeFilterCount = validFilters ? validFilters.filters.length : 0

  const ref = React.useRef<HTMLDivElement>(null)
  useOnClickOutside(ref, () => {
    setShowBody(false)
  })

  const onFilterButtonClick = useCallback(() => {
    setShowBody(!showBody)
  }, [showBody, setShowBody])

  const onCompoundFilterChange = useCallback(
    (filterGroup: ReportFilterGroup) => {
      sendChatReportFilterTrackingEvent(reportType, filterGroup?.filters || [])
      setCompoundFilter(filterGroup)
    },
    [setCompoundFilter, reportType]
  )

  return (
    <div
      className="compound-filter--container"
      ref={ref}
      data-ocweb-id="ReportsCompoundFilterButton"
    >
      <Button
        className={cx('compound-filter--dropdown-button', {
          open: showBody,
        })}
        variant="secondary"
        onClick={onFilterButtonClick}
      >
        <Button.Icon icon={faFilter} />
        {activeFilterCount > 0
          ? `${activeFilterCount} Filter${activeFilterCount > 1 ? 's' : ''}`
          : 'Filter'}
      </Button>
      {showBody && (
        <CompoundFilterBody
          reportType={reportType}
          availableFilterableEntities={availableFilterableEntities}
          compoundFilter={compoundFilter}
          setCompoundFilter={onCompoundFilterChange}
          onDeleteFinalRow={() => setShowBody(false)}
          allowOnlyOne={allowOnlyOne}
        />
      )}
    </div>
  )
}

interface IReportsCompoundFilterBarProps {
  availableFilterableEntities: FilterableEntityType[]
  reportAmounts?: number
  reportType: ReportPage
  allowOnlyOne?: boolean
  defaultState?: ReportFilterGroup
}

const EMPTY_FILTERS = { filters: [], shouldMatch: 0, negateFilterGroup: false }

const ReportsCompoundFilterBar: React.FC<IReportsCompoundFilterBarProps> = ({
  availableFilterableEntities,
  reportAmounts,
  reportType,
  allowOnlyOne = false,
  defaultState,
}) => {
  const compoundFilter = useSelector(FilterSelectors.compoundFilter)
  const [validFilters, setValidFilters] = useState(compoundFilter)
  const allFilterableEntities = useFilterableEntities(reportType)

  const allTeams = useSelector(teams)

  const dispatch = useDispatch()

  const defaultStateRef = useRef(defaultState)

  const onDeleteFilterRow = useCallback(
    (index) => {
      const newFilters = compoundFilter.filters
      newFilters.splice(index, 1)
      const newCompoundFilter = {
        ...compoundFilter,
        filters: newFilters,
      }
      dispatch(
        FilterActions.updateCompoundFilter({
          newCompoundFilter,
          teams: allTeams,
          reportType,
        })
      )
    },
    [compoundFilter, allTeams, reportType, dispatch]
  )

  const clearAllFilters = useCallback(() => {
    const newCompoundFilter: ReportFilterGroup = EMPTY_FILTERS
    dispatch(
      FilterActions.updateCompoundFilter({
        newCompoundFilter,
        teams: allTeams,
        reportType,
      })
    )
  }, [allTeams, reportType, dispatch])

  const resetFilters = useCallback(
    (defaultValue) => {
      const newCompoundFilter: ReportFilterGroup = defaultValue
      dispatch(
        FilterActions.updateCompoundFilter({
          newCompoundFilter,
          teams: allTeams,
          reportType,
        })
      )
    },
    [allTeams, reportType, dispatch]
  )

  useEffect(() => {
    // clear filter or set to default when report type changes
    if (defaultStateRef.current) {
      resetFilters(defaultStateRef.current)
    } else {
      clearAllFilters()
    }
  }, [reportType, clearAllFilters, defaultStateRef, resetFilters])

  useEffect(() => {
    if (compoundFilter) {
      const validFilters = compoundFilter.filters.filter(
        (filter) => !isEmpty(filter.values)
      )
      const newCompoundFilter = { ...compoundFilter, filters: validFilters }
      setValidFilters(newCompoundFilter)
    }
  }, [compoundFilter])

  const setCompoundFilter = (newValue) => {
    dispatch(
      FilterActions.updateCompoundFilter({
        newCompoundFilter: newValue,
        teams: allTeams,
        reportType,
      })
    )
  }

  return (
    <Div
      className="compound-filter-bar--container"
      data-ocweb-id="ReportsCompoundFilterBar"
    >
      {availableFilterableEntities.length > 0 && (
        <ReportsCompoundFilter
          reportType={reportType}
          compoundFilter={compoundFilter}
          validFilters={validFilters}
          setCompoundFilter={setCompoundFilter}
          availableFilterableEntities={availableFilterableEntities}
          allowOnlyOne={allowOnlyOne}
        />
      )}
      <Div>
        {reportAmounts && validFilters && validFilters.filters.length > 0 && (
          <Span className="compound-filter-bar--separator" />
        )}
        {validFilters &&
          validFilters.filters.map((filter, index) => (
            <Chip
              key={`${filterToComponent(
                filter,
                allFilterableEntities
              )}${index}`}
              label={filterToComponent(filter, allFilterableEntities)}
              onRemoveClick={() => onDeleteFilterRow(index)}
            />
          ))}
        {validFilters && validFilters.filters.length > 0 && (
          <Span
            className="compound-filter-bar--clear-all-link tide-anchor-link"
            onClick={clearAllFilters}
          >
            Clear all
          </Span>
        )}
      </Div>
    </Div>
  )
}

export default ReportsCompoundFilterBar
